import { useState } from "react";
import { Coffee } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";

// -----------------------------------------------------------------------------
// MealPrefsCell — inline editor for internal meal annotations on a booking.
// * ☕ Colazione toggle (breakfast on/off)
// * 4 chip toggles: GF (arancione), V (verde), VG (verde scuro), A (rosso)
// Persists via PATCH /bookings/{id}/meal-prefs — works for direct AND OTA
// rows (OTA ids are auto-prefixed with `ota-` upstream). These fields are
// internal-only: never propagated to Alloggiati Web (backend saves them on
// bookings / ical_events.manual_overrides, not on guest_registrations).
// -----------------------------------------------------------------------------
const FLAG_STYLES = {
  GF: { on: "bg-orange-500 text-white border-orange-500", off: "text-orange-600 border-orange-400/40 hover:bg-orange-500/10", title_it: "Senza glutine", title_en: "Gluten free" },
  V:  { on: "bg-green-500 text-white border-green-500",   off: "text-green-600 border-green-400/40 hover:bg-green-500/10",  title_it: "Vegetariano",   title_en: "Vegetarian" },
  VG: { on: "bg-green-800 text-white border-green-800",   off: "text-green-800 border-green-700/40 hover:bg-green-700/10",  title_it: "Vegano",        title_en: "Vegan" },
  A:  { on: "bg-red-500 text-white border-red-500",       off: "text-red-600 border-red-400/40 hover:bg-red-500/10",        title_it: "Allergie",      title_en: "Allergies" },
};

export default function MealPrefsCell({ booking, IT, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [breakfast, setBreakfast] = useState(!!booking.breakfast);
  const [flags, setFlags] = useState(Array.isArray(booking.dietary_flags) ? booking.dietary_flags : []);

  const patch = async (payload) => {
    setSaving(true);
    try {
      // OTA rows arrive with plain id; server expects `ota-` prefix for OTA.
      const bid = booking.is_ota && !String(booking.id).startsWith("ota-")
        ? `ota-${booking.id}`
        : booking.id;
      await api.patch(`/bookings/${encodeURIComponent(bid)}/meal-prefs`, payload);
      onSaved?.(booking.id, payload);
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
      // Roll back local state
      setBreakfast(!!booking.breakfast);
      setFlags(Array.isArray(booking.dietary_flags) ? booking.dietary_flags : []);
    } finally { setSaving(false); }
  };

  const toggleBreakfast = () => {
    const next = !breakfast;
    setBreakfast(next);
    patch({ breakfast: next });
  };

  const toggleFlag = (f) => {
    const next = flags.includes(f) ? flags.filter((x) => x !== f) : [...flags, f];
    setFlags(next);
    patch({ dietary_flags: next });
  };

  return (
    <div className="inline-flex items-center gap-1.5 flex-wrap" data-testid={`meal-prefs-${booking.id}`}>
      <button
        type="button"
        onClick={toggleBreakfast}
        disabled={saving}
        title={IT
          ? (breakfast ? "Colazione inclusa — clicca per rimuovere" : "Colazione non inclusa — clicca per aggiungere")
          : (breakfast ? "Breakfast included — click to remove" : "No breakfast — click to add")}
        className={`inline-flex items-center justify-center w-7 h-7 rounded-full border transition-all lift ${breakfast
          ? "bg-amber-500 text-white border-amber-500"
          : "bg-card text-muted-foreground border-dashed border-border hover:border-amber-500/60 hover:text-amber-600"}`}
        data-testid={`meal-breakfast-${booking.id}`}
        aria-pressed={breakfast}
        aria-label={IT ? "Colazione" : "Breakfast"}
      >
        <Coffee className="w-3.5 h-3.5"/>
      </button>
      {(["GF", "V", "VG", "A"]).map((f) => {
        const on = flags.includes(f);
        const style = FLAG_STYLES[f];
        return (
          <button
            key={f}
            type="button"
            onClick={() => toggleFlag(f)}
            disabled={saving}
            title={IT ? style.title_it : style.title_en}
            className={`inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full border font-mono text-[10px] font-bold transition-all lift ${on ? style.on : `bg-card ${style.off}`}`}
            data-testid={`meal-flag-${f}-${booking.id}`}
            aria-pressed={on}
          >
            {f}
          </button>
        );
      })}
    </div>
  );
}
